import { call, ForkEffect, put, select, StrictEffect, takeLatest } from 'redux-saga/effects';
import { TAction, TBoundAction } from '../../../../../../shared/frontend/scripts/actions/types/action';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import _ from 'lodash';

type TQueryModuleActions<Data, Params> = {
  load: (
    params: Params,
    options?: TQueryModuleHookOptions
  ) => TAction<{ params: Params; options?: TQueryModuleHookOptions }>;
  reset: () => TAction<void>;
  update: (data: TQueryModuleState<Data, Params>) => TAction<TQueryModuleState<Data, Params>>;
};

type TQueryModuleActionTypes = {
  LOAD: string;
  UPDATE: string;
  RESET: string;
};

type TQueryModuleActionMethods<Data, Params> =
  | TBoundAction<TQueryModuleActionTypes['LOAD'], Params>
  | TBoundAction<TQueryModuleActionTypes['UPDATE'], TQueryModuleState<Data, Params>>
  | TBoundAction<TQueryModuleActionTypes['RESET'], void>;

type TQueryModuleReducer<Data, Params> = (
  state: TQueryModuleState<Data, Params>,
  action: TQueryModuleActionMethods<Data, Params>
) => TQueryModuleState<Data, Params>;

type TQueryModuleLoadSaga<Data, Params> = (
  action: TBoundAction<TQueryModuleActionTypes['LOAD'], { params: Params; options?: TQueryModuleHookOptions }>
) => Generator<StrictEffect, void, any>;

type TQueryModule<Data, Params> = {
  actions: TQueryModuleActions<Data, Params>;
  actionTypes: TQueryModuleActionTypes;
  reducer: TQueryModuleReducer<Data, Params>;
  sagas: ForkEffect<never>[];
  fetchFn: (params: Params) => Promise<Data>;
  isParamsValid: (params: Params) => boolean;
};

export type TQueryModuleState<Data, Params> = {
  data?: Data;
  params?: Params;
  prevParams?: Params;
  isLoading: boolean;
  isLoaded: boolean;
  error?: Error | null;
};

type TCreateQueryModuleConfig<Data, Params> = {
  prefix: string;
  stateKey: string;
  initialState?: TQueryModuleState<Data, Params>;
  fetchFn: (params: Params) => Promise<Data>;
  isParamsValid?: (params: Params) => boolean;
};

const INITIAL_STATE: TQueryModuleState<any, any> = Object.freeze({
  data: undefined,
  params: undefined,
  isLoading: false,
  isLoaded: false,
  error: null
});

export const createQueryModule = <Data, Params>(
  config: TCreateQueryModuleConfig<Data, Params>
): TQueryModule<Data, Params> => {
  const { prefix, stateKey, initialState, fetchFn, isParamsValid } = config;

  const normalizedInitialState = Object.assign({}, INITIAL_STATE, initialState) as TQueryModuleState<Data, Params>;

  const actionTypes: TQueryModule<Data, Params>['actionTypes'] = {
    LOAD: `${prefix}/LOAD`,
    UPDATE: `${prefix}/UPDATE`,
    RESET: `${prefix}/RESET`
  };

  const actions: TQueryModuleActions<Data, Params> = {
    load: (params, options) => ({ type: actionTypes.LOAD, payload: { params, options } }),
    update: (data) => ({ type: actionTypes.UPDATE, payload: data }),
    reset: () => ({ type: actionTypes.RESET, payload: undefined })
  };

  const loadSaga: TQueryModuleLoadSaga<Data, Params> = function* (action) {
    const state = yield select((globalState: any) => globalState[stateKey]);

    try {
      yield put(
        actions.update({
          isLoading: true,
          isLoaded: false,
          error: null,
          params: action.payload.params,
          prevParams: state.params
        })
      );
      const data = yield call(fetchFn, action.payload.params);
      yield put(actions.update({ data, isLoading: false, isLoaded: true }));
    } catch (error) {
      yield put(actions.update({ error, isLoading: false, isLoaded: true }));
    }
  };

  const sagas = [takeLatest(actionTypes.LOAD, loadSaga)];

  const reducer: TQueryModuleReducer<Data, Params> = (state = normalizedInitialState, action) => {
    switch (action.type) {
      case actionTypes.LOAD:
        return { ...state, isLoading: true };

      case actionTypes.UPDATE:
        return { ...state, ...action.payload };

      case actionTypes.RESET:
        return { ...normalizedInitialState };

      default:
        return state;
    }
  };

  return {
    actions,
    actionTypes,
    reducer,
    sagas,
    fetchFn,
    isParamsValid: isParamsValid || (() => true)
  };
};

type TQueryModuleHook<Data, Params> = (
  params: Params,
  options?: TQueryModuleHookOptions
) => TQueryModuleState<Data, Params>;

type TCreateQueryModuleHookConfig<Data, Params> = {
  queryModule: TQueryModule<Data, Params>;
  /**
   * The key of the query module in the redux state.
   */
  key: string;
};

type TQueryModuleHookOptions = {
  keepPreviousData?: boolean;
};

const DEFAULT_QUERY_MODULE_HOOK_OPTIONS: TQueryModuleHookOptions = Object.freeze({
  keepPreviousData: false
});

export const createQueryModuleHook =
  <Data, Params>(config: TCreateQueryModuleHookConfig<Data, Params>): TQueryModuleHook<Data, Params> =>
  (params: Params, options?: TQueryModuleHookOptions) => {
    const normalizedOptions = { ...DEFAULT_QUERY_MODULE_HOOK_OPTIONS, ...options };
    const { queryModule } = config;
    const { actions } = queryModule;
    const dispatch = useDispatch();

    const state = useSelector((globalState: any) => globalState[config.key]) as TQueryModuleState<Data, Params>;

    useEffect(() => {
      const cleanParamsObject = <T>(obj: T): T => {
        if (obj === null || obj === undefined) {
          return obj;
        }

        if (typeof obj !== 'object') {
          return obj;
        }

        if (Array.isArray(obj)) {
          return obj.filter((item) => item !== null && item !== undefined).map((item) => cleanParamsObject(item)) as T;
        }

        const result = {} as T;
        for (const [key, value] of Object.entries(obj)) {
          if (value !== null && value !== undefined) {
            result[key as keyof T] = cleanParamsObject(value);
          }
        }
        return result;
      };

      const isEmptyState = !state.isLoading && !state.isLoaded;
      const isMatchParams = state.isLoaded && _.isEqual(cleanParamsObject(state.params), cleanParamsObject(params));

      const isValid = queryModule.isParamsValid(params);

      if (!isValid) {
        return;
      }

      if (state.isLoading && !isMatchParams) {
        return;
      }

      if (isEmptyState || !isMatchParams) {
        dispatch(actions.load(params, normalizedOptions));
      }
    }, [params]);

    return state;
  };

export const createServerPreloadActionsByQueryModule =
  <Data, Params>(queryModule: TQueryModule<Data, Params>) =>
  async (params: Params): Promise<TAction<any>[]> => {
    const isValid = queryModule.isParamsValid(params);

    if (!isValid) {
      return [];
    }

    try {
      const result = await queryModule.fetchFn(params);
      return [queryModule.actions.update({ data: result, isLoading: false, isLoaded: true, params })];
    } catch (error) {
      return [queryModule.actions.update({ error, isLoading: false, isLoaded: true, params })];
    }
  };
